import { useState, useEffect, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../lib/cartStore";
import { formatCents, calculateEntryTotal, catalogVersion, isVceSchoolLicence } from "../lib/products";
import { SchoolSelector } from "../components/SchoolSelector";
import { QuoteLinkModal } from "../components/QuoteLinkModal";
import { DiscountLineInput, type ManualDiscount } from "../components/DiscountLineInput";
import type { CheckoutFormData, School } from "../lib/types";

const SALES_PASSWORD_KEY = "smartered-sales-password";

const emptyFormData: CheckoutFormData = {
  contact: {
    firstName: "",
    lastName: "",
    email: "",
    role: "",
  },
  school: {
    acaraId: undefined,
    name: "",
    state: undefined,
    isManualEntry: false,
  },
  adminFirstName: "",
  adminLastName: "",
  adminEmail: "",
  purchaseOrderNumber: "",
  notes: "",
  paymentMethod: "card",
};

interface FieldErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  school?: string;
  adminFirstName?: string;
  adminLastName?: string;
  adminEmail?: string;
}

export function CheckoutPage() {
  const navigate = useNavigate();
  const { entries, getTotals, getCartItems, clearCart, checkoutData, setCheckoutData, quoteContext, reorderContext } = useCart();

  // Use cart entries directly from context
  const cartEntries = entries;
  const totals = getTotals();

  // Track if we're navigating away after successful submission
  const [isNavigatingAway, setIsNavigatingAway] = useState(false);

  // Determine if school is locked (from quote or reorder link)
  const isSchoolLocked = !!(quoteContext || reorderContext);
  const lockedSchool = quoteContext
    ? { acaraId: quoteContext.schoolId, name: quoteContext.schoolName }
    : reorderContext
      ? { acaraId: reorderContext.schoolId, name: reorderContext.schoolName }
      : null;

  // Build initial form data, pre-filling from quote context if available
  const buildInitialFormData = (): CheckoutFormData => {
    // Start with persisted checkout data or empty
    const base = checkoutData || emptyFormData;

    // Pre-fill from quote context if available
    if (quoteContext) {
      const result = { ...base };

      // Pre-fill contact if available and not already filled
      if (quoteContext.contact && !base.contact.email) {
        result.contact = {
          firstName: quoteContext.contact.firstName,
          lastName: quoteContext.contact.lastName,
          email: quoteContext.contact.email,
          phone: quoteContext.contact.phone || "",
          role: base.contact.role || "",
        };
      }

      // Pre-fill school if locked
      if (lockedSchool) {
        result.school = { acaraId: lockedSchool.acaraId, name: lockedSchool.name, isManualEntry: false };
      }

      // Pre-fill admin details if available
      if (quoteContext.adminEmail && !base.adminEmail) {
        result.adminFirstName = quoteContext.adminFirstName || base.adminFirstName || "";
        result.adminLastName = quoteContext.adminLastName || base.adminLastName || "";
        result.adminEmail = quoteContext.adminEmail;
      }

      // Pre-fill payment method if available
      if (quoteContext.paymentMethod) {
        result.paymentMethod = quoteContext.paymentMethod;
      }

      return result;
    }

    // Pre-fill school from locked context if available
    if (lockedSchool && !base.school.acaraId) {
      return {
        ...base,
        school: { acaraId: lockedSchool.acaraId, name: lockedSchool.name, isManualEntry: false },
      };
    }

    return base;
  };

  // Initialize form with persisted checkout data or empty
  const [formData, setFormData] = useState<CheckoutFormData>(buildInitialFormData);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showQuoteLinkModal, setShowQuoteLinkModal] = useState(false);
  const [isSalesUser, setIsSalesUser] = useState(false);
  const [manualDiscounts, setManualDiscounts] = useState<ManualDiscount[]>(
    quoteContext?.manualDiscounts || []
  );

  // Check if sales user is authenticated
  useEffect(() => {
    const hasPassword = !!sessionStorage.getItem(SALES_PASSWORD_KEY);
    setIsSalesUser(hasPassword);
  }, []);

  // Persist form data to cart context when it changes
  useEffect(() => {
    setCheckoutData(formData);
  }, [formData, setCheckoutData]);

  // Redirect to home if cart is empty (unless we're navigating to confirmation).
  // NOTE: this guard must stay BELOW all hook calls — an early return above the
  // hooks violated react-hooks/rules-of-hooks (conditional hook order).
  if (cartEntries.length === 0 && !isNavigatingAway) {
    navigate("/", { replace: true });
    return null;
  }

  const validateField = (field: string, value: string): string | undefined => {
    switch (field) {
      case "firstName":
        return value.trim() ? undefined : "First name is required";
      case "lastName":
        return value.trim() ? undefined : "Last name is required";
      case "email":
        if (!value.trim()) return "Email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Please enter a valid email";
        return undefined;
      case "school":
        return value.trim() ? undefined : "School is required";
      case "adminFirstName":
        return value.trim() ? undefined : "First name is required";
      case "adminLastName":
        return value.trim() ? undefined : "Last name is required";
      case "adminEmail":
        if (!value.trim()) return "Email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Please enter a valid email";
        return undefined;
      default:
        return undefined;
    }
  };

  const handleInputChange = (field: string, value: string) => {
    if (field.startsWith("contact.")) {
      const contactField = field.replace("contact.", "");
      setFormData((prev) => ({
        ...prev,
        contact: { ...prev.contact, [contactField]: value },
      }));
      // Clear error when user types
      if (touched[contactField]) {
        const error = validateField(contactField, value);
        setFieldErrors((prev) => ({ ...prev, [contactField]: error }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear error when user types (for adminEmail field)
      if (touched[field]) {
        const error = validateField(field, value);
        setFieldErrors((prev) => ({ ...prev, [field]: error }));
      }
    }
  };

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));

    let value = "";
    if (field === "school") {
      value = formData.school.name;
    } else if (field === "firstName") {
      value = formData.contact.firstName;
    } else if (field === "lastName") {
      value = formData.contact.lastName;
    } else if (field === "email") {
      value = formData.contact.email;
    } else if (field === "adminFirstName") {
      value = formData.adminFirstName || "";
    } else if (field === "adminLastName") {
      value = formData.adminLastName || "";
    } else if (field === "adminEmail") {
      value = formData.adminEmail || "";
    }

    const error = validateField(field, value);
    setFieldErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleSchoolSelect = (school: School | null, isManual: boolean, manualName?: string) => {
    if (school) {
      setFormData((prev) => ({
        ...prev,
        school: {
          acaraId: school.acaraId,
          name: school.name,
          state: school.state,
          isManualEntry: false,
        },
      }));
      setFieldErrors((prev) => ({ ...prev, school: undefined }));
    } else if (isManual && manualName) {
      setFormData((prev) => ({
        ...prev,
        school: {
          acaraId: undefined,
          name: manualName,
          state: undefined,
          isManualEntry: true,
        },
      }));
      setFieldErrors((prev) => ({ ...prev, school: manualName.trim() ? undefined : "School is required" }));
    } else {
      // Clear selection (when "Change" is clicked)
      setFormData((prev) => ({
        ...prev,
        school: {
          acaraId: undefined,
          name: "",
          state: undefined,
          isManualEntry: false,
        },
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {
      firstName: validateField("firstName", formData.contact.firstName),
      lastName: validateField("lastName", formData.contact.lastName),
      email: validateField("email", formData.contact.email),
      // Skip school validation if locked from quote/reorder context
      school: isSchoolLocked ? undefined : validateField("school", formData.school.name),
      adminFirstName: validateField("adminFirstName", formData.adminFirstName || ""),
      adminLastName: validateField("adminLastName", formData.adminLastName || ""),
      adminEmail: validateField("adminEmail", formData.adminEmail || ""),
    };

    setFieldErrors(errors);
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      school: true,
      adminFirstName: true,
      adminLastName: true,
      adminEmail: true,
    });

    // Build summary of errors for the banner
    const errorFields = Object.entries(errors)
      .filter(([, error]) => error !== undefined)
      .map(([field]) => {
        const labels: Record<string, string> = {
          firstName: "First name",
          lastName: "Last name",
          email: "Email",
          school: "School",
          adminFirstName: "Admin first name",
          adminLastName: "Admin last name",
          adminEmail: "Admin email",
        };
        return labels[field] || field;
      });

    if (errorFields.length > 0) {
      setSubmitError(`Please fix the following: ${errorFields.join(", ")}`);
    }

    return errorFields.length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      // Scroll to first error
      const firstError = document.querySelector(".form-group.has-error, .has-error");
      firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      // Build items from cart entries
      const items = cartEntries.map((entry) => ({
        productId: entry.group.id,
        variantId: entry.variantId,
        quantity: entry.quantity,
      }));

      // Use locked school if available, otherwise use form data
      const schoolData = isSchoolLocked && lockedSchool
        ? { acaraId: lockedSchool.acaraId, name: lockedSchool.name, isManualEntry: false }
        : formData.school;

      // Build manual discounts payload (only non-zero discounts)
      const discountPayload = manualDiscounts
        .filter(d => d.amountCents > 0)
        .map(d => ({ description: d.description, amountCents: d.amountCents }));

      // H6 (docs/deep-analysis-2026-07-05.md): forward verification material so
      // the server sources discounts from a trusted origin instead of trusting
      // client-supplied manualDiscounts. Two verified sources:
      //  - a signed quote link (raw d/s params carried in quoteContext), or
      //  - the sales password (when a sales user hand-enters discounts).
      // The card handler (checkout-session) ignores these fields today —
      // forwarding them anyway future-proofs it for the same enforcement.
      const verificationPayload: Record<string, unknown> = {};
      if (quoteContext?.quoteData && quoteContext?.quoteSig) {
        verificationPayload.quoteData = quoteContext.quoteData;
        verificationPayload.quoteSig = quoteContext.quoteSig;
      } else if (isSalesUser && discountPayload.length > 0) {
        // NOTE: the API expects snake_case `sales_password` (same field name as
        // POST /api/orders/quote-link).
        const salesPassword = sessionStorage.getItem(SALES_PASSWORD_KEY);
        if (salesPassword) {
          verificationPayload.sales_password = salesPassword;
        }
      }

      // H6: shared error handling for both submission endpoints.
      const throwSubmitError = async (response: Response, isJson: unknown, unavailableMessage: string): Promise<never> => {
        if (isJson) {
          const errorData = await response.json().catch(() => ({}));
          if (response.status === 401 && errorData.error === "invalid_credentials") {
            // Stored sales password no longer verifies server-side — clear it so
            // the discount editor is re-gated until the user logs in again.
            sessionStorage.removeItem(SALES_PASSWORD_KEY);
            setIsSalesUser(false);
            throw new Error(
              errorData.message
                ? `${errorData.message} Please log in again via the sales portal.`
                : "Sales credentials are invalid. Please log in again via the sales portal."
            );
          }
          if (response.status === 400 && errorData.error === "invalid_quote") {
            // Surface the server's reason (e.g. quote expired, or the cart no
            // longer matches the quoted items).
            throw new Error(errorData.message || "This quote link is no longer valid for the current cart.");
          }
          throw new Error(errorData.message || unavailableMessage);
        }
        throw new Error(unavailableMessage);
      };

      if (formData.paymentMethod === "card") {
        // Create Stripe Checkout session
        const response = await fetch("/api/direct/checkout-session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contact: formData.contact,
            school: schoolData,
            adminFirstName: formData.adminFirstName || undefined,
            adminLastName: formData.adminLastName || undefined,
            adminEmail: formData.adminEmail || undefined,
            purchaseOrderNumber: formData.purchaseOrderNumber,
            notes: formData.notes,
            items,
            catalogVersion,  // For stale cart detection
            ...(discountPayload.length > 0 ? { manualDiscounts: discountPayload } : {}),
            ...verificationPayload,
          }),
        });

        const contentType = response.headers.get("content-type");
        const isJson = contentType && contentType.includes("application/json");

        if (!response.ok) {
          await throwSubmitError(response, isJson, "Checkout service unavailable. Please try again later.");
        }

        if (!isJson) {
          throw new Error("Checkout service unavailable. Please try again later.");
        }

        const { sessionUrl } = await response.json();
        // Redirect to Stripe Checkout
        window.location.href = sessionUrl;
      } else {
        // Create invoice order
        const response = await fetch("/api/direct/order", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contact: formData.contact,
            school: schoolData,
            adminFirstName: formData.adminFirstName,
            adminLastName: formData.adminLastName,
            adminEmail: formData.adminEmail,
            purchaseOrderNumber: formData.purchaseOrderNumber,
            notes: formData.notes,
            items,
            catalogVersion,  // For stale cart detection
            ...(discountPayload.length > 0 ? { manualDiscounts: discountPayload } : {}),
            ...verificationPayload,
          }),
        });

        const contentType = response.headers.get("content-type");
        const isJson = contentType && contentType.includes("application/json");

        if (!response.ok) {
          await throwSubmitError(response, isJson, "Order service unavailable. Please try again later.");
        }

        if (!isJson) {
          throw new Error("Order service unavailable. Please try again later.");
        }

        const orderData = await response.json();

        // Capture cart data before clearing (for confirmation page)
        const confirmationEntries = [...cartEntries];
        const confirmationTotals = { ...totals };

        // Set flag before clearing cart to prevent redirect race condition
        setIsNavigatingAway(true);
        clearCart();

        navigate("/order/confirmation", {
          state: {
            agreementNumber: orderData.agreementNumber,
            paymentMethod: "invoice",
            school: schoolData,
            contact: formData.contact,
            adminFirstName: formData.adminFirstName,
            adminLastName: formData.adminLastName,
            adminEmail: formData.adminEmail,
            entries: confirmationEntries,
            totals: confirmationTotals,
          },
        });
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "An error occurred");
      setSubmitting(false);
    }
  };

  const getFieldClass = (field: string) => {
    if (touched[field] && fieldErrors[field as keyof FieldErrors]) {
      return "form-group has-error";
    }
    return "form-group";
  };

  return (
    <div className="order-page">
      <header className="order-header">
        <img src="https://smartered.wpengine.com/wp-content/uploads/2023/01/SmarterEd-logo.svg" alt="SmarterEd" className="order-header-logo" />
        <div className="order-header-text">
          <h1>Checkout</h1>
          <p>Complete your order details</p>
        </div>
      </header>

      {submitError && <div className="error-banner">{submitError}</div>}

      <div className="checkout-layout">
        <main className="checkout-form-section">
          <form onSubmit={handleSubmit} noValidate>
            {/* Contact Details */}
            <section className="checkout-card">
              <h2 className="checkout-card-title">Subscription Contact</h2>

              <div className="form-row">
                <div className={getFieldClass("firstName")}>
                  <label htmlFor="firstName">First name *</label>
                  <input
                    id="firstName"
                    type="text"
                    value={formData.contact.firstName}
                    onChange={(e) => handleInputChange("contact.firstName", e.target.value)}
                    onBlur={() => handleBlur("firstName")}
                    aria-invalid={!!fieldErrors.firstName}
                  />
                  {touched.firstName && fieldErrors.firstName && (
                    <span className="field-error">{fieldErrors.firstName}</span>
                  )}
                </div>
                <div className={getFieldClass("lastName")}>
                  <label htmlFor="lastName">Last name *</label>
                  <input
                    id="lastName"
                    type="text"
                    value={formData.contact.lastName}
                    onChange={(e) => handleInputChange("contact.lastName", e.target.value)}
                    onBlur={() => handleBlur("lastName")}
                    aria-invalid={!!fieldErrors.lastName}
                  />
                  {touched.lastName && fieldErrors.lastName && (
                    <span className="field-error">{fieldErrors.lastName}</span>
                  )}
                </div>
              </div>

              <div className={getFieldClass("email")}>
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="name@school.edu.au"
                  value={formData.contact.email}
                  onChange={(e) => handleInputChange("contact.email", e.target.value)}
                  onBlur={() => handleBlur("email")}
                  aria-invalid={!!fieldErrors.email}
                />
                {touched.email && fieldErrors.email && (
                  <span className="field-error">{fieldErrors.email}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone (optional)</label>
                <input
                  id="phone"
                  type="tel"
                  placeholder="e.g., 02 1234 5678"
                  value={formData.contact.phone || ""}
                  onChange={(e) => handleInputChange("contact.phone", e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="role">Role (optional)</label>
                <input
                  id="role"
                  type="text"
                  placeholder="e.g., Head of Mathematics"
                  value={formData.contact.role}
                  onChange={(e) => handleInputChange("contact.role", e.target.value)}
                />
              </div>
            </section>

            {/* School */}
            <section className="checkout-card">
              <h2 className="checkout-card-title">School</h2>
              {isSchoolLocked && lockedSchool ? (
                <div className="school-selected">
                  <div className="school-selected-info">
                    <span className="school-selected-name">{lockedSchool.name}</span>
                    <span className="school-selected-location">ACARA ID: {lockedSchool.acaraId}</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className={touched.school && fieldErrors.school ? "has-error" : ""}>
                    <SchoolSelector
                      onSelect={handleSchoolSelect}
                      selectedSchool={
                        formData.school.acaraId
                          ? { acaraId: formData.school.acaraId, name: formData.school.name }
                          : null
                      }
                      manualEntry={formData.school.isManualEntry ? formData.school.name : undefined}
                    />
                    {touched.school && fieldErrors.school && (
                      <span className="field-error">{fieldErrors.school}</span>
                    )}
                  </div>
                  {/* NSW government school notification */}
                  {formData.school.name && (formData.school.state === "NSW" || formData.school.isManualEntry) && (
                    <div className="info-banner">
                      NSW Government schools must purchase through the NSW DoE Marketplace. This does not apply to other schools.
                    </div>
                  )}
                </>
              )}
            </section>

            {/* Teacher Licence Administrator */}
            <section className="checkout-card">
              <h2 className="checkout-card-title">Teacher Licence Administrator</h2>
              <p className="checkout-card-description">
                Specify an initial teacher who will manage teacher licences for
                your school. This person will be able to assign and unassign
                teachers to courses. Also note that any teacher assigned to a
                course will then be able to administer other teacher licences.
              </p>

              <div className="form-row">
                <div className={getFieldClass("adminFirstName")}>
                  <label htmlFor="adminFirstName">First name *</label>
                  <input
                    id="adminFirstName"
                    type="text"
                    value={formData.adminFirstName || ""}
                    onChange={(e) => handleInputChange("adminFirstName", e.target.value)}
                    onBlur={() => handleBlur("adminFirstName")}
                    aria-invalid={!!fieldErrors.adminFirstName}
                  />
                  {touched.adminFirstName && fieldErrors.adminFirstName && (
                    <span className="field-error">{fieldErrors.adminFirstName}</span>
                  )}
                </div>
                <div className={getFieldClass("adminLastName")}>
                  <label htmlFor="adminLastName">Last name *</label>
                  <input
                    id="adminLastName"
                    type="text"
                    value={formData.adminLastName || ""}
                    onChange={(e) => handleInputChange("adminLastName", e.target.value)}
                    onBlur={() => handleBlur("adminLastName")}
                    aria-invalid={!!fieldErrors.adminLastName}
                  />
                  {touched.adminLastName && fieldErrors.adminLastName && (
                    <span className="field-error">{fieldErrors.adminLastName}</span>
                  )}
                </div>
              </div>

              <div className={getFieldClass("adminEmail")}>
                <label htmlFor="adminEmail">Email *</label>
                <input
                  id="adminEmail"
                  type="email"
                  autoComplete="email"
                  placeholder="admin.teacher@school.edu.au"
                  value={formData.adminEmail || ""}
                  onChange={(e) => handleInputChange("adminEmail", e.target.value)}
                  onBlur={() => handleBlur("adminEmail")}
                  aria-invalid={!!fieldErrors.adminEmail}
                />
                {touched.adminEmail && fieldErrors.adminEmail && (
                  <span className="field-error">{fieldErrors.adminEmail}</span>
                )}
              </div>

              <p className="field-hint">
                This person will receive instructions on how to manage teacher licences.
              </p>
            </section>

            {/* Additional Details */}
            <section className="checkout-card">
              <h2 className="checkout-card-title">Additional Details</h2>

              <div className="form-group">
                <label htmlFor="poNumber">Purchase Order Number (optional)</label>
                <input
                  id="poNumber"
                  type="text"
                  placeholder="If required by your school"
                  value={formData.purchaseOrderNumber}
                  onChange={(e) => handleInputChange("purchaseOrderNumber", e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notes (optional)</label>
                <textarea
                  id="notes"
                  rows={3}
                  placeholder="Any additional information"
                  value={formData.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                />
              </div>
            </section>

            {/* Payment Method */}
            <section className="checkout-card">
              <h2 className="checkout-card-title">Payment Method</h2>

              <div className="payment-options">
                <label className={`payment-option ${formData.paymentMethod === "card" ? "selected" : ""}`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="card"
                    checked={formData.paymentMethod === "card"}
                    onChange={() => setFormData((prev) => ({ ...prev, paymentMethod: "card" }))}
                  />
                  <div className="payment-option-content">
                    <span className="payment-option-title">Pay by card</span>
                    <span className="payment-option-desc">Secure payment via Stripe.</span>
                  </div>
                </label>

                <label className={`payment-option ${formData.paymentMethod === "invoice" ? "selected" : ""}`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="invoice"
                    checked={formData.paymentMethod === "invoice"}
                    onChange={() => setFormData((prev) => ({ ...prev, paymentMethod: "invoice" }))}
                  />
                  <div className="payment-option-content">
                    <span className="payment-option-title">Invoice me</span>
                    <span className="payment-option-desc">We'll email you an invoice, payable by card or EFT.</span>
                  </div>
                </label>
              </div>
            </section>

            <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={submitting}>
              {submitting
                ? "Processing..."
                : formData.paymentMethod === "card"
                  ? "Proceed to payment"
                  : "Place order"}
            </button>
          </form>
        </main>

        {/* Order Summary */}
        <aside className="checkout-summary-section">
          <div className="checkout-summary">
            <h2 className="checkout-summary-title">Order Summary</h2>

            {(() => {
              const allItems = getCartItems();
              const autoDiscountItems = allItems.filter((item) => item.isDiscount);
              const hasAutoDiscounts = autoDiscountItems.length > 0;
              const hasManualDiscounts = manualDiscounts.length > 0 && manualDiscounts.some(d => d.amountCents > 0);
              const hasDiscounts = hasAutoDiscounts || hasManualDiscounts;

              // Calculate subtotal before discounts (positive items only)
              const subtotalBeforeDiscounts = cartEntries.reduce(
                (sum, entry) => sum + calculateEntryTotal(entry.group, entry.quantity, entry.variantId, entry.isAddingToExisting),
                0
              );

              // Calculate manual discount total
              const manualDiscountTotal = manualDiscounts.reduce((sum, d) => sum + d.amountCents, 0);

              // Calculate adjusted totals including manual discounts
              const adjustedSubtotalExGst = totals.subtotalExGstCents - manualDiscountTotal;
              const adjustedGst = Math.round(adjustedSubtotalExGst * 0.1);
              const adjustedTotalIncGst = adjustedSubtotalExGst + adjustedGst;

              return (
                <>
                  {/* Line items with prices */}
                  <div className="checkout-summary-items">
                    {cartEntries.map((entry) => {
                      const lineTotal = calculateEntryTotal(entry.group, entry.quantity, entry.variantId, entry.isAddingToExisting);
                      const isStudentProduct = entry.group.productType === "student";
                      const unitLabel = isStudentProduct ? "block" : "teacher";
                      const unitsLabel = isStudentProduct ? "blocks" : "teachers";
                      // A VCE school licence reads as one licence; the request still sends its 99 seats.
                      const quantityLabel = isVceSchoolLicence(entry.group)
                        ? "1 school licence"
                        : `${entry.quantity} ${entry.quantity === 1 ? unitLabel : unitsLabel}`;

                      return (
                        <div key={entry.groupId} className="checkout-summary-item">
                          <div className="checkout-summary-item-info">
                            <div className="checkout-summary-item-name">
                              {entry.group.name}
                              {entry.variantId && (
                                <span className="checkout-summary-item-variant">
                                  {" "}— {entry.variantId.charAt(0).toUpperCase() + entry.variantId.slice(1)}
                                </span>
                              )}
                            </div>
                            <div className="checkout-summary-item-detail">
                              {quantityLabel}
                            </div>
                          </div>
                          <div className="checkout-summary-item-price">
                            {formatCents(lineTotal)}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Subtotal before discounts (only show if there are discounts) */}
                  {hasDiscounts && (
                    <div className="checkout-summary-subtotal-row">
                      <span>Subtotal</span>
                      <span>{formatCents(subtotalBeforeDiscounts)}</span>
                    </div>
                  )}

                  {/* Bundle Discounts and Manual Discounts */}
                  {hasDiscounts && (
                    <div className="checkout-summary-discounts">
                      {hasAutoDiscounts && (
                        <>
                          <div className="checkout-summary-discounts-header">Bundle Discounts</div>
                          {autoDiscountItems.map((discount, idx) => (
                            <div key={idx} className="checkout-summary-discount-row">
                              <span className="checkout-summary-discount-name">{discount.name}</span>
                              <span className="checkout-summary-discount-amount">{formatCents(discount.unitPriceExGstCents)}</span>
                            </div>
                          ))}
                        </>
                      )}
                      {hasManualDiscounts && (
                        <>
                          <div className="checkout-summary-discounts-header" style={{ marginTop: hasAutoDiscounts ? "12px" : "0" }}>
                            Manual Discounts
                          </div>
                          {manualDiscounts.filter(d => d.amountCents > 0).map((discount, idx) => (
                            <div key={`manual-${idx}`} className="checkout-summary-discount-row">
                              <span className="checkout-summary-discount-name" style={{ fontStyle: "italic" }}>
                                {discount.description || "Discount"}
                              </span>
                              <span className="checkout-summary-discount-amount">{formatCents(-discount.amountCents)}</span>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}

                  {/* Final totals */}
                  <div className="checkout-summary-totals">
                    {!hasDiscounts && (
                      <div className="checkout-summary-row">
                        <span>Subtotal</span>
                        <span>{formatCents(adjustedSubtotalExGst)}</span>
                      </div>
                    )}
                    <div className="checkout-summary-row">
                      <span>GST (10%)</span>
                      <span>{formatCents(adjustedGst)}</span>
                    </div>
                    <div className="checkout-summary-row checkout-summary-total">
                      <span>Total</span>
                      <span>{formatCents(adjustedTotalIncGst)}</span>
                    </div>
                  </div>
                </>
              );
            })()}

            <button
              type="button"
              className="btn btn-secondary btn-full"
              onClick={() => navigate("/")}
              disabled={submitting}
            >
              Edit order
            </button>

            {isSalesUser && (
              <>
                <button
                  type="button"
                  className="btn btn-secondary btn-full"
                  onClick={() => setShowQuoteLinkModal(true)}
                  disabled={submitting}
                  style={{ marginTop: "8px" }}
                >
                  Generate quote link
                </button>

                <DiscountLineInput
                  discounts={manualDiscounts}
                  onChange={setManualDiscounts}
                />
              </>
            )}
          </div>
        </aside>
      </div>

      {showQuoteLinkModal && (
        <QuoteLinkModal
          schoolId={isSchoolLocked && lockedSchool ? lockedSchool.acaraId : formData.school.acaraId}
          schoolName={isSchoolLocked && lockedSchool ? lockedSchool.name : formData.school.name}
          items={cartEntries.map(entry => ({
            product_id: entry.group.id,
            variant_id: entry.variantId,
            quantity: entry.quantity,
          }))}
          contact={{
            firstName: formData.contact.firstName,
            lastName: formData.contact.lastName,
            email: formData.contact.email,
            phone: formData.contact.phone,
          }}
          adminFirstName={formData.adminFirstName}
          adminLastName={formData.adminLastName}
          adminEmail={formData.adminEmail}
          paymentMethod={formData.paymentMethod}
          discounts={manualDiscounts.filter(d => d.amountCents > 0)}
          onClose={() => setShowQuoteLinkModal(false)}
        />
      )}
    </div>
  );
}
